import { useState, useMemo, useEffect, Fragment } from "react";
import { AlertTriangle, ChevronDown, ChevronRight } from "lucide-react";
import { computeNpd } from "@/utils/npdCalc";
import { formatINR } from "@/utils/formatINR";
import api from "@/lib/api";

export default function RoundCompareView({ round, currentNpd, rightLabel = "Current", npdId }) {
  const [expanded, setExpanded] = useState({});

  const then = useMemo(() => computeNpd(round.snapshot || {}), [round]);
  const now = useMemo(() => computeNpd(currentNpd || {}), [currentNpd]);

  const sections = [
    {
      key: "net_rmc",
      label: "A. Net Raw Material",
      thenVal: then.net_rmc,
      nowVal: now.net_rmc,
      warningNarrative: "Net RMC moved by 10% or more. Verify material grade, gross/net weight, RM rate, and scrap recovery factor — these are the inputs that drive this section.",
    },
    {
      key: "process_cost_total",
      label: "B. Process Cost",
      thenVal: then.process_cost_total,
      nowVal: now.process_cost_total,
      warningNarrative: "Process Cost moved by 10% or more. Check whether process steps were added or removed, or rates changed — confirm against production data and operations master.",
    },
    {
      key: "bop_cost_total",
      label: "C. Bought Out Parts",
      thenVal: then.bop_cost_total,
      nowVal: now.bop_cost_total,
      warningNarrative: "BOP cost moved by 10% or more. A bought-out item may have been added, removed, or repriced — verify the BOM.",
    },
    {
      key: "treatment_total",
      label: "D. Treatment + Tool Maint",
      thenVal: (then.treatment_total || 0) + (then.tool_amortization || 0),
      nowVal: (now.treatment_total || 0) + (now.tool_amortization || 0),
      warningNarrative: "Treatment + Tool Maintenance moved by 10% or more. Verify surface/heat treatment mode and rate, tool maintenance %, and whether tool amortization was toggled.",
    },
    {
      key: "overheads_and_margin",
      label: "E. Overheads & Margin",
      thenVal: then.overheads_and_margin,
      nowVal: now.overheads_and_margin,
      warningNarrative: "Overheads & Margin moved by 10% or more. Margin assumptions may have shifted — verify the % column or whether any lump-sum rows were edited or removed.",
    },
  ];

  const toggle = (key) => setExpanded((p) => ({ ...p, [key]: !p[key] }));

  const computeDelta = (a, b) => {
    const d = (Number(b) || 0) - (Number(a) || 0);
    const pct = Math.abs(a) > 0.0001 ? (d / a) * 100 : 0;
    return { abs: d, pct };
  };

  const DeltaAbs = ({ d }) => {
    if (Math.abs(d) < 0.005) return <span className="text-slate-400">—</span>;
    const sign = d > 0 ? "+" : "-";
    const color = d < 0 ? "text-emerald-600" : "text-red-600";
    return <span className={color}>{sign}{formatINR(Math.abs(d))}</span>;
  };

  const DeltaPct = ({ pct }) => {
    if (Math.abs(pct) < 0.05) return <span className="text-slate-400">—</span>;
    const sign = pct > 0 ? "+" : "";
    const color = pct < 0 ? "text-emerald-600" : "text-red-600";
    return <span className={color}>{sign}{pct.toFixed(1)}%</span>;
  };

  const totalDelta = computeDelta(then.grand_total, now.grand_total);

  return (
    <div className="mt-3 bg-white border border-sky-300 rounded-md overflow-hidden" data-testid="round-compare-view">
      <div className="px-3 py-2 bg-sky-50 border-b border-sky-200 text-[11px] text-slate-600 flex items-center gap-3 flex-wrap">
        <span><strong className="text-sky-700">{round.round_label}</strong> vs {rightLabel}</span>
        {round.round_date && (<><span className="text-slate-500">·</span><span>Saved {new Date(round.round_date).toLocaleString("en-IN")}</span></>)}
        {round.notes && (<><span className="text-slate-500">·</span><span className="italic truncate">"{round.notes}"</span></>)}
      </div>
      {/* R44 — Drawing snapshot strip for this round (left side of compare) */}
      {npdId && (
        <RoundDrawingStrip
          npdId={npdId}
          round={round}
          data-testid={`round-drawing-row-${round.round_id}`}
        />
      )}
      <table className="w-full text-xs">
        <thead className="bg-sky-50 text-slate-600">
          <tr>
            <th className="text-left p-2 font-medium">Section</th>
            <th className="text-right p-2 font-medium w-24">Then</th>
            <th className="text-right p-2 font-medium w-24">Now</th>
            <th className="text-right p-2 font-medium w-28">Δ ₹</th>
            <th className="text-right p-2 font-medium w-20">Δ %</th>
            <th className="w-10 p-2"></th>
          </tr>
        </thead>
        <tbody className="divide-y divide-slate-100">
          {sections.map((s) => {
            const d = computeDelta(s.thenVal, s.nowVal);
            const isLargeChange = Math.abs(d.pct) >= 10;
            const isExpanded = expanded[s.key];
            return (
              <Fragment key={s.key}>
                <tr className={isLargeChange ? "bg-amber-50/60" : "hover:bg-slate-50"}>
                  <td className="p-2">
                    <button
                      type="button"
                      onClick={() => toggle(s.key)}
                      className="flex items-center gap-1 text-left"
                      data-testid={`compare-section-${s.key}`}
                    >
                      {isExpanded ? <ChevronDown size={12} className="text-slate-400 flex-shrink-0" /> : <ChevronRight size={12} className="text-slate-400 flex-shrink-0" />}
                      <span className="font-medium text-slate-700">{s.label}</span>
                    </button>
                  </td>
                  <td className="p-2 text-right font-mono text-slate-600">{formatINR(s.thenVal)}</td>
                  <td className="p-2 text-right font-mono text-slate-900">{formatINR(s.nowVal)}</td>
                  <td className="p-2 text-right font-mono"><DeltaAbs d={d.abs} /></td>
                  <td className="p-2 text-right font-mono"><DeltaPct pct={d.pct} /></td>
                  <td className="p-2 text-center">
                    {isLargeChange && (
                      <div className="relative group inline-block">
                        <AlertTriangle size={14} className="text-amber-600 cursor-help" data-testid={`warn-${s.key}`} />
                        <div className="hidden group-hover:block absolute z-20 right-0 top-5 w-64 p-2 bg-slate-800 text-white text-[11px] rounded shadow-lg leading-snug normal-case">
                          {s.warningNarrative}
                        </div>
                      </div>
                    )}
                  </td>
                </tr>
                {isExpanded && (
                  <tr>
                    <td colSpan={6} className="bg-slate-50/60 p-2 border-t border-slate-200">
                      <SectionDetail sectionKey={s.key} thenNpd={round.snapshot || {}} nowNpd={currentNpd || {}} />
                    </td>
                  </tr>
                )}
              </Fragment>
            );
          })}
        </tbody>
        <tfoot className="bg-slate-100 border-t-2 border-slate-300">
          <tr>
            <td className="p-2 font-bold text-slate-900">GRAND TOTAL</td>
            <td className="p-2 text-right font-mono font-bold text-slate-700">{formatINR(then.grand_total)}</td>
            <td className="p-2 text-right font-mono font-bold text-slate-900">{formatINR(now.grand_total)}</td>
            <td className="p-2 text-right font-mono font-bold"><DeltaAbs d={totalDelta.abs} /></td>
            <td className="p-2 text-right font-mono font-bold"><DeltaPct pct={totalDelta.pct} /></td>
            <td></td>
          </tr>
        </tfoot>
      </table>
      <div className="px-3 py-2 bg-slate-50 text-[10px] text-slate-500 italic border-t border-slate-200">
        Read-only. Amber warning marks sections that moved 10% or more — hover for what to check. Click a section label to drill into line-item changes.
      </div>
    </div>
  );
}

function SectionDetail({ sectionKey, thenNpd, nowNpd }) {
  if (sectionKey === "net_rmc") {
    const rows = [
      ["Material", thenNpd.material_grade || "—", nowNpd.material_grade || "—"],
      ["Thickness (mm)", thenNpd.thickness_mm || 0, nowNpd.thickness_mm || 0],
      ["Strip Width (mm)", thenNpd.strip_width || 0, nowNpd.strip_width || 0],
      ["Pitch (mm)", thenNpd.pitch || 0, nowNpd.pitch || 0],
      ["Gross Weight (kg)", thenNpd.gross_weight || 0, nowNpd.gross_weight || 0],
      ["Net Weight (kg)", thenNpd.net_weight || 0, nowNpd.net_weight || 0],
      ["RM Rate (₹/kg)", thenNpd.rm_price_per_kg || 0, nowNpd.rm_price_per_kg || 0],
      ["Scrap Rate (₹/kg)", thenNpd.scrap_price_per_kg || 0, nowNpd.scrap_price_per_kg || 0],
    ];
    return <KeyValueDiff rows={rows} />;
  }
  if (sectionKey === "process_cost_total") {
    return (
      <ListDiff
        thenList={thenNpd.processes || []}
        nowList={nowNpd.processes || []}
        keyFn={(p) => p.name || "(unnamed)"}
        valueFn={(p) => `${p.cost_mode || "per_piece"} · ₹${Number(p.rate || p.cost || 0).toFixed(2)}`}
      />
    );
  }
  if (sectionKey === "bop_cost_total") {
    return (
      <ListDiff
        thenList={thenNpd.bop_items || []}
        nowList={nowNpd.bop_items || []}
        keyFn={(b) => b.name || "(unnamed)"}
        valueFn={(b) => `${Number(b.qty || 0)} × ₹${Number(b.unit_price || 0).toFixed(2)}`}
      />
    );
  }
  if (sectionKey === "treatment_total") {
    const T = (t) => {
      if (!t || typeof t !== "object") return `₹${Number(t || 0).toFixed(2)}`;
      const base = `${t.mode || "per_piece"} · ₹${Number(t.rate || 0).toFixed(2)}`;
      return t.notes ? `${base} · ${t.notes}` : base;
    };
    const rows = [
      ["Surface Treatment", T(thenNpd.surface_treatment ?? thenNpd.surface_treatment_cost), T(nowNpd.surface_treatment ?? nowNpd.surface_treatment_cost)],
      ["Heat Treatment", T(thenNpd.heat_treatment ?? thenNpd.heat_treatment_cost), T(nowNpd.heat_treatment ?? nowNpd.heat_treatment_cost)],
      ["Tool Maintenance %", `${Number(thenNpd.tool_maintenance_pct ?? 10).toFixed(1)}%`, `${Number(nowNpd.tool_maintenance_pct ?? 10).toFixed(1)}%`],
      ["Amortize Tooling", thenNpd.amortize_tooling ? "ON" : "OFF", nowNpd.amortize_tooling ? "ON" : "OFF"],
      ["Tooling Cost (₹)", Number(thenNpd.tooling_cost || 0).toLocaleString("en-IN"), Number(nowNpd.tooling_cost || 0).toLocaleString("en-IN")],
      ["Tooling Life (cycles)", Number(thenNpd.tooling_life || 0).toLocaleString("en-IN"), Number(nowNpd.tooling_life || 0).toLocaleString("en-IN")],
    ];
    return <KeyValueDiff rows={rows} />;
  }
  if (sectionKey === "overheads_and_margin") {
    return (
      <ListDiff
        thenList={thenNpd.overheads || []}
        nowList={nowNpd.overheads || []}
        keyFn={(o) => o.label || "(unnamed)"}
        valueFn={(o) => `${o.basis || "sub_total"} · ${Number(o.value || 0)}${o.basis === "lump_sum" ? " ₹" : ""}`}
      />
    );
  }
  return null;
}

function KeyValueDiff({ rows }) {
  return (
    <table className="w-full text-xs">
      <tbody>
        {rows.map(([label, thenV, nowV], i) => {
          const changed = String(thenV) !== String(nowV);
          return (
            <tr key={i} className={changed ? "bg-amber-50" : ""}>
              <td className="py-1 px-1.5 text-slate-500 w-1/3 align-top">{label}</td>
              <td className={`py-1 px-1.5 font-mono align-top break-words ${changed ? "text-slate-400 line-through" : "text-slate-600"}`}>{thenV}</td>
              <td className={`py-1 px-1.5 font-mono align-top break-words ${changed ? "font-semibold text-slate-900" : "text-slate-600"}`}>{nowV}</td>
            </tr>
          );
        })}
      </tbody>
    </table>
  );
}

function ListDiff({ thenList, nowList, keyFn, valueFn }) {
  const thenMap = new Map((thenList || []).map((x) => [keyFn(x), valueFn(x)]));
  const nowMap = new Map((nowList || []).map((x) => [keyFn(x), valueFn(x)]));
  const allKeys = Array.from(new Set([...thenMap.keys(), ...nowMap.keys()]));
  if (allKeys.length === 0) return <div className="text-xs text-slate-400 italic py-1">No items in either round.</div>;
  return (
    <table className="w-full text-xs">
      <tbody>
        {allKeys.map((k) => {
          const t = thenMap.get(k);
          const n = nowMap.get(k);
          let badge = null;
          if (t === undefined) badge = <span className="text-[9px] bg-emerald-100 text-emerald-700 px-1 rounded ml-1">added</span>;
          else if (n === undefined) badge = <span className="text-[9px] bg-red-100 text-red-700 px-1 rounded ml-1">removed</span>;
          else if (t !== n) badge = <span className="text-[9px] bg-amber-100 text-amber-700 px-1 rounded ml-1">changed</span>;
          const changed = !!badge;
          return (
            <tr key={k} className={changed ? "bg-amber-50" : ""}>
              <td className="py-1 px-1.5 text-slate-700 w-1/3 align-top truncate">{k}{badge}</td>
              <td className={`py-1 px-1.5 font-mono align-top break-words ${changed ? "text-slate-400 line-through" : "text-slate-600"}`}>{t ?? "—"}</td>
              <td className={`py-1 px-1.5 font-mono align-top break-words ${changed ? "font-semibold text-slate-900" : "text-slate-600"}`}>{n ?? "—"}</td>
            </tr>
          );
        })}
      </tbody>
    </table>
  );
}


// R44 — Per-round drawing & screenshot thumbnail strip
function RoundDrawingStrip({ npdId, round }) {
  const [screenshotUrl, setScreenshotUrl] = useState(null);
  const [drawingDownloadUrl, setDrawingDownloadUrl] = useState(null);
  const isCurrent = !round.round_id || round.round_id === "_current_";
  const snap = round.snapshot || {};
  const hasScreenshot = !!snap.drawing_screenshot_path;
  const hasDrawing = !!snap.drawing_file_path;

  useEffect(() => {
    let revoke;
    if (!hasScreenshot) { setScreenshotUrl(null); return; }
    const url = isCurrent
      ? `/npd/${npdId}/screenshot`
      : `/npd/${npdId}/rounds/${round.round_id}/screenshot`;
    api.get(url, { responseType: "blob" })
      .then((r) => {
        const b = URL.createObjectURL(r.data);
        revoke = b;
        setScreenshotUrl(b);
      })
      .catch(() => setScreenshotUrl(null));
    return () => { if (revoke) URL.revokeObjectURL(revoke); };
  }, [npdId, round.round_id, hasScreenshot, isCurrent]);

  useEffect(() => {
    let revoke;
    if (!hasDrawing) { setDrawingDownloadUrl(null); return; }
    const url = isCurrent
      ? `/npd/${npdId}/drawing`
      : `/npd/${npdId}/rounds/${round.round_id}/drawing`;
    api.get(url, { responseType: "blob" })
      .then((r) => {
        const b = URL.createObjectURL(r.data);
        revoke = b;
        setDrawingDownloadUrl(b);
      })
      .catch(() => setDrawingDownloadUrl(null));
    return () => { if (revoke) URL.revokeObjectURL(revoke); };
  }, [npdId, round.round_id, hasDrawing, isCurrent]);

  return (
    <div
      className="px-3 py-3 flex flex-wrap items-start gap-4 bg-slate-50 border-b border-slate-200"
      data-testid={`round-drawing-row-${round.round_id || "current"}`}
    >
      <div className="flex-shrink-0">
        {hasScreenshot && screenshotUrl ? (
          <a href={screenshotUrl} target="_blank" rel="noopener noreferrer" title="Open screenshot in new tab">
            <img
              src={screenshotUrl}
              alt={`${round.round_label} drawing screenshot`}
              className="w-32 h-32 object-cover border border-slate-300 rounded shadow-sm hover:shadow-md transition-shadow"
            />
          </a>
        ) : (
          <div className="w-32 h-32 flex items-center justify-center text-[10px] text-slate-400 border border-dashed border-slate-300 rounded bg-white">
            {hasScreenshot ? "Loading…" : "No screenshot"}
          </div>
        )}
      </div>
      <div className="flex-1 min-w-[200px]">
        <div className="text-xs uppercase tracking-wider text-slate-500 mb-1">Drawing for {round.round_label}</div>
        {hasDrawing && drawingDownloadUrl ? (
          <a
            href={drawingDownloadUrl}
            download={snap.drawing_original_filename || "drawing"}
            target="_blank"
            rel="noopener noreferrer"
            className="text-sm text-orange-600 hover:text-orange-700 underline"
          >
            Download source drawing
            {snap.drawing_original_filename ? ` (${snap.drawing_original_filename})` : ""}
          </a>
        ) : hasDrawing ? (
          <div className="text-xs text-slate-400 italic">Loading drawing…</div>
        ) : (
          <div className="text-xs text-slate-400 italic">No source drawing on this round.</div>
        )}
        <div className="text-[11px] text-slate-500 mt-2">
          {isCurrent
            ? "Current live drawing on this NPD."
            : `This is the drawing that was current when ${round.round_label} was saved${round.round_date ? ` on ${new Date(round.round_date).toLocaleString("en-IN")}` : ""}. Subsequent uploads on the NPD do not affect this round.`}
        </div>
      </div>
    </div>
  );
}
